import { useStore } from 'hooks/useStore';
import {
  errorDescriptionInterface,
  moleculeStrengthOptionsInterface,
  portfolioOpportunitiesDataInterface,
  User,
} from 'Interfaces/BDPortalInterfaces';
import { Nullable } from 'primereact/ts-helpers';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { bdportalScreenServices } from 'services/bdportalScreenServices';
import { deepCopy } from 'utils/deepCopy';

interface optionsInterface {
  code: string;
  name: string;
}

const useDataValidationComponent = ({
  data,
  successCallBack,
  moleculeStrengthOptionsData,
}: {
  data: portfolioOpportunitiesDataInterface;
  successCallBack: () => void;
  moleculeStrengthOptionsData: moleculeStrengthOptionsInterface[];
}) => {
  const [bulkUpdateData, setBulkUpdateData] = useState<portfolioOpportunitiesDataInterface>(data);
  const [errorDescriptions, setErrorDescriptions] = useState<errorDescriptionInterface[]>([]);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const {
    BDPortalStore: { dropdownDetails, board, getAllOpportunity },
  } = useStore();

  // sourceOptions is set for potential future use with source dropdown
  const [, setSourceOptions] = useState<string[]>([]);

  const [selectedRowSet, setSelectedRowsSet] = useState<Set<number>>(new Set());
  const [moleculeStrengthOptions, setMoleculeStrengthOptions] = useState<Map<string, string[]>>(
    new Map()
  );
  const [moleculesStrengthsLoading, setMoleculeStrengthsLoading] = useState<Set<string>>(new Set());
  const [rowsStrengthCommentAccordionOpened, setRowsStrengthCommentAccordionOpened] = useState<
    Set<number>
  >(new Set());

  const getAssigneedOptions = () => {
    if (dropdownDetails) {
      const tempOptions: User[] = [];

      tempOptions.push({
        ID: 0,
        Name: 'Unassigned',
      });
      dropdownDetails['NC'].User?.forEach(option => {
        tempOptions.push(option);
      });
      return tempOptions;
    }
    return [];
  };

  const opportunityTypeOptions: optionsInterface[] = [
    {
      code: 'NC',
      name: 'NC',
    },
    {
      code: 'MAS',
      name: 'MAS',
    },
  ];

  const handleVerify = async () => {
    try {
      setIsBulkUpdating(true);

      const result = await bdportalScreenServices.savePortfolioOpportunities(bulkUpdateData);

      if ('message' in result && result['message'] === 'successfull') {
        await getAllOpportunity(board);
        toast.success('Successfully Created Portfolio Opportunties !');
        setRowsStrengthCommentAccordionOpened(new Set());
        setErrorDescriptions([]);

        const tempData: portfolioOpportunitiesDataInterface = [];

        bulkUpdateData.forEach(instance => {
          if (!instance.creation_status) {
            tempData.push(instance);
          }
        });

        if (tempData.length === 0) {
          successCallBack();
        }
        setBulkUpdateData(tempData);
        setSelectedRowsSet(new Set());
      } else if ('error' in result && result['error'] === true) {
        setErrorDescriptions(result.error_descriptions);
      }
    } catch {
      toast.error('Failed to Create Portfolio Opportunities');
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const checkIfThereAreErrors = () => {
    return errorDescriptions.length > 0;
  };

  const formUpdateMethods = {
    handleUpdateSource: (source: number) => {
      const tempBulkUdateData = deepCopy(bulkUpdateData);
      for (let i = 0; i < bulkUpdateData.length; i++) {
        tempBulkUdateData[i].source = source;
      }
      setBulkUpdateData(tempBulkUdateData);
    },

    handleUpdateSupplier: (supplier: string) => {
      const tempBulkUdateData = deepCopy(bulkUpdateData);
      for (let i = 0; i < bulkUpdateData.length; i++) {
        tempBulkUdateData[i].supplier = supplier;
      }
      setBulkUpdateData(tempBulkUdateData);
    },

    handleUpdateOpportunityType: (rowIndex: number, type: string) => {
      if (rowIndex < bulkUpdateData.length) {
        const tempBulkUdateData = deepCopy(bulkUpdateData);
        tempBulkUdateData[rowIndex].opportunity_type = type;
        setBulkUpdateData(tempBulkUdateData);
      }
    },

    handleUpdateNdc: (rowIndex: number, _comment: string) => {
      if (rowIndex < bulkUpdateData.length) {
        const tempBulkUdateData = deepCopy(bulkUpdateData);
        // bulkUpdateData[rowIndex]. = analyst;
        setBulkUpdateData(tempBulkUdateData);
      }
    },

    handleUpdateMolecule: (rowIndex: number, molecule: string) => {
      if (rowIndex < bulkUpdateData.length) {
        const tempBulkUdateData = deepCopy(bulkUpdateData);
        tempBulkUdateData[rowIndex].molecule = molecule;
        setBulkUpdateData(tempBulkUdateData);
        const tempRowsStrengthCommentAccordionOpened = new Set(rowsStrengthCommentAccordionOpened);
        tempRowsStrengthCommentAccordionOpened.add(rowIndex);
        setRowsStrengthCommentAccordionOpened(tempRowsStrengthCommentAccordionOpened);
      }
    },

    handleUpdateOpportunityToBeCreatedOrNot: (rowIndex: number, status: boolean) => {
      if (rowIndex < bulkUpdateData.length) {
        const tempBulkUdateData = deepCopy(bulkUpdateData);
        tempBulkUdateData[rowIndex].creation_status = status;
        setBulkUpdateData(tempBulkUdateData);
      }
    },

    handleUpdateAnalyst: (rowIndex: number, analyst: number) => {
      if (rowIndex < bulkUpdateData.length) {
        const tempBulkUdateData = deepCopy(bulkUpdateData);
        tempBulkUdateData[rowIndex].analyst = analyst;
        setBulkUpdateData(tempBulkUdateData);
      }
    },

    handleUpdateComments: (rowIndex: number, comments: Nullable<string[]>) => {
      if (rowIndex < bulkUpdateData.length) {
        if (comments && comments.length > bulkUpdateData[rowIndex].data.length) {
          toast.error(
            `${bulkUpdateData[rowIndex].data.length} note can be added since there are only ${bulkUpdateData[rowIndex].data.length} entries merged from file uploaded`
          );
          return;
        }

        const tempBulkUdateData = deepCopy(bulkUpdateData);
        for (let i = 0; i < tempBulkUdateData[rowIndex].data.length; i++) {
          tempBulkUdateData[rowIndex].data[i].comments = '';
        }

        if (comments) {
          for (let i = 0; i < comments.length && i < tempBulkUdateData[rowIndex].data.length; i++) {
            tempBulkUdateData[rowIndex].data[i].comments = comments[i];
          }
        }

        setBulkUpdateData(tempBulkUdateData);
      }
    },

    handleUpdateStrengthNdcPacksize: (
      rowIndex: number,
      value: { strength: string; packsize: number; ndc: string | null }[]
    ) => {
      if (value.length === bulkUpdateData[rowIndex].data.length) {
        const tempBulkUdateData = deepCopy(bulkUpdateData);
        for (let i = 0; i < tempBulkUdateData[rowIndex].data.length; i++) {
          tempBulkUdateData[rowIndex].data[i].strength = value[i].strength;
          tempBulkUdateData[rowIndex].data[i].pack_size = value[i].packsize;
          tempBulkUdateData[rowIndex].data[i].ndc = value[i].ndc;
        }
        setBulkUpdateData(tempBulkUdateData);
      }
    },

    handleUpdateCreationStatus: (rowIndex: number, status: boolean) => {
      if (rowIndex < bulkUpdateData.length) {
        const tempBulkUdateData = deepCopy(bulkUpdateData);
        tempBulkUdateData[rowIndex].creation_status = status;
        setBulkUpdateData(tempBulkUdateData);
      }
    },

    handleUpdateCreationStatusForAllRows: (status: boolean) => {
      const tempBulkUdateData = deepCopy(bulkUpdateData);
      for (let i = 0; i < tempBulkUdateData.length; i++) {
        tempBulkUdateData[i].creation_status = status;
      }
      setBulkUpdateData(tempBulkUdateData);
    },
  };

  const abortControllerRef = useRef<AbortController | null>(null);

  interface Option {
    label: string;
    value: string;
  }

  const inputOperations = {
    source: {
      value: () => {
        return bulkUpdateData[0].source;
      },
      onChange: (value: number) => {
        formUpdateMethods.handleUpdateSource(value);
      },
      options: () => {
        const options: string[] = [];
        dropdownDetails?.NC.Source.forEach(instance => {
          options.push(instance.Source);
        });
        return options;
      },
    },
    suppier: {
      value: () => {
        return bulkUpdateData.length > 0 ? bulkUpdateData[0].supplier : null;
      },
      onChange: (value: string) => {
        formUpdateMethods.handleUpdateSupplier(value);
      },
    },
    opportunityType: {
      value: (rowIndex: number) => {
        return {
          code: bulkUpdateData[rowIndex].opportunity_type,
          name: bulkUpdateData[rowIndex].opportunity_type,
        };
      },
      onChange: (rowIndex: number, value: string) => {
        formUpdateMethods.handleUpdateOpportunityType(rowIndex, value);
      },
    },
    ndc: {
      value: (rowIndex: number) => {
        return bulkUpdateData[rowIndex].data[0].ndc;
      },
      onChange: (rowIndex: number, value: string) => {
        formUpdateMethods.handleUpdateNdc(rowIndex, value);
      },
    },
    molecule: {
      value: (rowIndex: number) => {
        return bulkUpdateData[rowIndex].molecule;
      },
      onChange: (rowIndex: number, value: string) => {
        formUpdateMethods.handleUpdateMolecule(rowIndex, value);
        const tempRowsStrengthCommentAccordionOpened = new Set(rowsStrengthCommentAccordionOpened);
        tempRowsStrengthCommentAccordionOpened.add(rowIndex);
        setRowsStrengthCommentAccordionOpened(tempRowsStrengthCommentAccordionOpened);
        getMoleculeStrengthsData(value);
      },
      fetchSuggestions:
        () =>
        async (inputValue: string): Promise<Option[]> => {
          // Cancel any ongoing request if there is one
          if (abortControllerRef.current) {
            abortControllerRef.current.abort(); // Abort the previous request
          }

          // Create a new AbortController instance for the new request
          const controller = new AbortController();
          abortControllerRef.current = controller;

          return new Promise(resolve => {
            setTimeout(async () => {
              try {
                const response = await bdportalScreenServices.getMoleculeSuggestions(
                  inputValue,
                  controller.signal
                );
                const suggestions: Option[] = [];
                response.forEach(item => {
                  suggestions.push({
                    label: item.MoleculeAndForm,
                    value: item.MoleculeAndForm,
                  });
                });
                resolve(suggestions);
              } catch (error) {
                console.error('Error fetching suggestions:', error);
                resolve([]); // Resolve with an empty array in case of an error
              }
            }, 500); // Delay of 500ms
          });
        },
    },
    opportunityCreationStatus: {
      value: (rowIndex: number) => {
        const code = bulkUpdateData[rowIndex].creation_status ? 'Yes' : 'No';
        return {
          code: code,
          name: code,
        };
      },
      onChange: (rowIndex: number, value: string) => {
        formUpdateMethods.handleUpdateOpportunityToBeCreatedOrNot(
          rowIndex,
          value.toLowerCase() === 'yes' ? true : false
        );
      },
    },
    analyst: {
      value: (rowIndex: number) => {
        return bulkUpdateData[rowIndex].analyst;
      },
      onChange: (rowIndex: number, value: number) => {
        formUpdateMethods.handleUpdateAnalyst(rowIndex, value);
      },
    },

    strength: {
      value: (rowIndex: number) => {
        const strengthPacksizeNDCCombination: string[] = [];
        bulkUpdateData[rowIndex].data.forEach(instance => {
          strengthPacksizeNDCCombination.push(
            `${instance.pack_size} -- ${instance.strength} -- ${instance.ndc}`
          );
        });
        return strengthPacksizeNDCCombination;
      },
      // onChange: (rowIndex: number, value: string) => {
      //   console.log({ rowIndex, value });
      //   formUpdateMethods.handleUpdateAnalyst(rowIndex, value);
      // },
    },

    strengthNdcPacksize: {
      value: (rowIndex: number) => {
        const tempData: {
          strength: string;
          packsize: number;
          ndc: string | null;
        }[] = [];
        for (let i = 0; i < bulkUpdateData[rowIndex].data.length; i++) {
          tempData.push({
            strength: bulkUpdateData[rowIndex].data[i].strength,
            packsize: bulkUpdateData[rowIndex].data[i].pack_size,
            ndc: bulkUpdateData[rowIndex].data[i].ndc,
          });
        }
        return tempData;
      },
      onChange: (
        rowIndex: number,
        value: { strength: string; packsize: number; ndc: string | null }[]
      ) => {
        formUpdateMethods.handleUpdateStrengthNdcPacksize(rowIndex, value);
      },
    },

    comments: {
      value: (rowIndex: number) => {
        const tempCommentsList: string[] = [];
        for (let i = 0; i < bulkUpdateData[rowIndex].data.length; i++) {
          if (bulkUpdateData[rowIndex].data[i].comments) {
            tempCommentsList.push(bulkUpdateData[rowIndex].data[i].comments);
          }
        }
        return tempCommentsList;
      },
      onChange: (rowIndex: number, value: Nullable<string[]>) => {
        formUpdateMethods.handleUpdateComments(rowIndex, value);
      },
      disabled: (rowIndex: number) => {
        if (
          inputOperations.comments.value(rowIndex).length === bulkUpdateData[rowIndex].data.length
        ) {
          return true;
        } else return false;
      },
    },
  };

  const fetchSuggestionsWithDelay = async (inputValue: string): Promise<Option[]> => {
    // Cancel any ongoing request if there is one
    if (abortControllerRef.current) {
      abortControllerRef.current.abort(); // Abort the previous request
    }

    // Create a new AbortController instance for the new request
    const controller = new AbortController();
    abortControllerRef.current = controller;

    return new Promise(resolve => {
      setTimeout(async () => {
        try {
          const response = await bdportalScreenServices.getMoleculeSuggestions(
            inputValue,
            controller.signal
          );
          const suggestions: Option[] = [];
          response.forEach(item => {
            suggestions.push({
              label: item.MoleculeAndForm,
              value: item.MoleculeAndForm,
            });
          });
          resolve(suggestions);
        } catch (error) {
          console.error('Error fetching suggestions:', error);
          resolve([]); // Resolve with an empty array in case of an error
        }
      }, 500); // Delay of 500ms
    });
  };

  const fetchSuggestionsWithDelayForSuppliers = async (inputValue: string): Promise<Option[]> => {
    // Cancel any ongoing request if there is one
    if (abortControllerRef.current) {
      abortControllerRef.current.abort(); // Abort the previous request
    }

    // Create a new AbortController instance for the new request
    const controller = new AbortController();
    abortControllerRef.current = controller;

    return new Promise(resolve => {
      setTimeout(async () => {
        try {
          const response = await bdportalScreenServices.getSuppliersSuggestions(
            inputValue,
            controller.signal
          );

          const suggestions: Option[] = [];
          response.forEach(item => {
            suggestions.push({
              label: item.SupplierName,
              value: item.SupplierName,
            });
          });
          resolve(suggestions);
        } catch (error) {
          console.error('Error fetching suggestions:', error);
          resolve([]); // Resolve with an empty array in case of an error
        }
      }, 500); // Delay of 500ms
    });
  };

  const handleRowDeleteButtonClick = (rowIndex: number) => {
    // delete the row from the bulkUpdateData
    if (rowIndex < bulkUpdateData.length) {
      const tempBulkUdateData = deepCopy(bulkUpdateData);
      tempBulkUdateData.splice(rowIndex, 1);
      setBulkUpdateData(tempBulkUdateData);
    }

    // fix the entries in the error description array

    const tempNewArrayDescription: errorDescriptionInterface[] = [];

    for (let i = 0; i < errorDescriptions.length; i++) {
      if (errorDescriptions[i].row - 1 < rowIndex) {
        tempNewArrayDescription.push(errorDescriptions[i]);
      } else if (errorDescriptions[i].row - 1 > rowIndex) {
        const editedErrorDescription = deepCopy(errorDescriptions[i]);
        editedErrorDescription.row = editedErrorDescription.row - 1;
        tempNewArrayDescription.push(editedErrorDescription);
      }
    }

    setErrorDescriptions(tempNewArrayDescription);

    const tempRowsStrengthCommentAccordionOpened = new Set(rowsStrengthCommentAccordionOpened);
    tempRowsStrengthCommentAccordionOpened.delete(rowIndex);
    setRowsStrengthCommentAccordionOpened(tempRowsStrengthCommentAccordionOpened);

    // Update the selected row indexes
    updateSelectedRowsOnRowDelete(rowIndex);
  };

  const getErrorDescription = (rowIndex: number) => {
    for (let i = 0; i < errorDescriptions.length; i++) {
      if (errorDescriptions[i].row === rowIndex + 1) {
        return errorDescriptions[i].description;
      }
    }
    return '';
  };

  const checkIfVerifyDataButtonIsDiabled = () => {
    if (selectedRowSet.size === 0) return true;
    return bulkUpdateData.length ? false : true;
  };

  const areAllRowsSelected = () => {
    return bulkUpdateData.length === selectedRowSet.size;
  };

  const handleRowSelectUnselect = (rowIndex: number) => {
    const tempSelectedRowIndexSet = new Set(selectedRowSet);

    if (selectedRowSet.has(rowIndex)) {
      tempSelectedRowIndexSet.delete(rowIndex);
      formUpdateMethods.handleUpdateCreationStatus(rowIndex, false);
    } else {
      tempSelectedRowIndexSet.add(rowIndex);
      formUpdateMethods.handleUpdateCreationStatus(rowIndex, true);
    }

    setSelectedRowsSet(tempSelectedRowIndexSet);
  };

  const updateSelectedRowsOnRowDelete = (rowIndex: number) => {
    const tempSelectedRowIndexSet: Set<number> = new Set();
    selectedRowSet.forEach(value => {
      if (value < rowIndex) {
        tempSelectedRowIndexSet.add(value);
      } else if (value > rowIndex) {
        tempSelectedRowIndexSet.add(value - 1);
      }
    });
    setSelectedRowsSet(tempSelectedRowIndexSet);
  };

  const handleCheckIfRowIsSelected = (rowIndex: number) => {
    return selectedRowSet.has(rowIndex);
  };

  const handleSelectAll = () => {
    if (areAllRowsSelected()) {
      setSelectedRowsSet(new Set());
      formUpdateMethods.handleUpdateCreationStatusForAllRows(false);
    } else {
      const tempSelectedRowIndexSet: Set<number> = new Set();

      for (let i = 0; i < bulkUpdateData.length; i++) {
        tempSelectedRowIndexSet.add(i);
      }
      setSelectedRowsSet(tempSelectedRowIndexSet);
      formUpdateMethods.handleUpdateCreationStatusForAllRows(true);
    }
  };

  const getMoleculeStrengthsData = async (moleculeName: string) => {
    // check if the strength options are already avaiable for the molecule name then do not fetch them again

    if (moleculeStrengthOptions.has(moleculeName)) {
      return;
    }

    try {
      const tempMoleculeStrengthsLoading = new Set(moleculesStrengthsLoading);
      setMoleculeStrengthsLoading(tempMoleculeStrengthsLoading);
      tempMoleculeStrengthsLoading.add(moleculeName);
      const response = await bdportalScreenServices.getStrengthSuggestions({
        molecule: moleculeName,
      });
      const tempMoleculeStrengthOptionsMap = new Map(moleculeStrengthOptions);
      tempMoleculeStrengthOptionsMap.set(
        moleculeName,
        response.map(val => val.toLowerCase())
      );
      setMoleculeStrengthOptions(tempMoleculeStrengthOptionsMap);
    } catch (_err) {
      toast.error('Error occured while fetching the strengths');
    } finally {
      const tempMoleculeStrengthsLoading = new Set(moleculesStrengthsLoading);
      tempMoleculeStrengthsLoading.delete(moleculeName);
      setMoleculeStrengthsLoading(tempMoleculeStrengthsLoading);
    }
  };

  const getRowStrengthOptions = (rowIndex: number) => {
    if (
      rowIndex < bulkUpdateData.length &&
      moleculeStrengthOptions.has(bulkUpdateData[rowIndex].molecule)
    ) {
      const options = moleculeStrengthOptions.get(bulkUpdateData[rowIndex].molecule) || [];
      return options.map(value => {
        return {
          code: value,
          name: value,
        };
      });
    }
    return [];
  };

  const checkIfStrengthsLoadingForRow = (rowIndex: number) => {
    if (
      rowIndex < bulkUpdateData.length &&
      moleculesStrengthsLoading.has(bulkUpdateData[rowIndex].molecule)
    ) {
      return true;
    }
    return false;
  };

  const handleClickRowOpenCloseStrengthCommentAccordion = (index: number) => {
    const tempStrengthCommentAccordion = new Set(rowsStrengthCommentAccordionOpened);
    if (tempStrengthCommentAccordion.has(index)) {
      tempStrengthCommentAccordion.delete(index);
    } else {
      tempStrengthCommentAccordion.add(index);
    }
    setRowsStrengthCommentAccordionOpened(tempStrengthCommentAccordion);
  };

  const checkIfRowAccordionStrengthAccordionOpen = (index: number) => {
    return rowsStrengthCommentAccordionOpened.has(index) ? true : false;
  };

  useEffect(() => {
    const options: string[] = [];
    dropdownDetails?.NC.Source.forEach(instance => {
      options.push(instance.Source);
    });
    setSourceOptions(options);
  }, [dropdownDetails]);

  useEffect(() => {
    const tempSelectedRowIndexSet: Set<number> = new Set();
    data.forEach((instance, index) => {
      if (instance.creation_status) {
        tempSelectedRowIndexSet.add(index);
      }
    });
    setSelectedRowsSet(tempSelectedRowIndexSet);
  }, [data]);

  // responsible for creating the moelcule strength map for the coming molecule strength combination data from the props
  useEffect(() => {
    const tempMoleculeStrengthOptionsMap: Map<string, string[]> = new Map();
    moleculeStrengthOptionsData.forEach(instance => {
      tempMoleculeStrengthOptionsMap.set(
        instance.molecule,
        instance.strengths.map(val => val.toLowerCase())
      );
    });
    setMoleculeStrengthOptions(tempMoleculeStrengthOptionsMap);
  }, [moleculeStrengthOptionsData]);

  return {
    bulkUpdateData,
    isBulkUpdating,
    checkIfThereAreErrors,
    handleVerify,
    handleRowDeleteButtonClick,
    getErrorDescription,
    getAssigneedOptions,
    dropdownDetails,
    inputOperations,
    checkIfVerifyDataButtonIsDiabled,
    areAllRowsSelected,
    handleRowSelectUnselect,
    handleCheckIfRowIsSelected,
    handleSelectAll,
    fetchSuggestionsWithDelay,
    opportunityTypeOptions,
    fetchSuggestionsWithDelayForSuppliers,
    checkIfStrengthsLoadingForRow,
    handleClickRowOpenCloseStrengthCommentAccordion,
    checkIfRowAccordionStrengthAccordionOpen,
    getRowStrengthOptions,
  };
};

export default useDataValidationComponent;
